import {
    Directive,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { IDashboardWidget } from "src/app/models/dashboard";
import { DashboardOverrideService } from "src/app/services/dashboard-override.service";
import { WidgetOrgResolverService } from "src/app/services/widget-org-resolver.service";
import { ORG_LEVEL_OVERRIDE_KEY } from "src/app/common-components/widget-org-filter/widget-org-filter.component";
import { WidgetOrgEffectService } from "./widget-org-effect.service";
import { EMPTY_WIDGET_TITLE_META, WidgetTitleMeta } from "src/app/models/dashboard/widget-title.model";

@Directive()
export abstract class BaseDashboardWidget
    implements OnInit, OnChanges, OnDestroy, IDashboardWidget
{
    @Input() widgetTitle?: string;
    @Input() titleMeta: WidgetTitleMeta = EMPTY_WIDGET_TITLE_META;
    @Input() widgetId?: string;
    @Input() widgetConfig: Record<string, unknown> = {};
    @Input() isLoading = false;
    @Output() configChange = new EventEmitter<Record<string, unknown>>();

    error?: string;

    protected destroy$ = new Subject<void>();
    protected widgetConfig$ = new BehaviorSubject<Record<string, unknown>>({});

    private widgetOrgEffect = inject(WidgetOrgEffectService);
    protected widgetOrgResolver = inject(WidgetOrgResolverService);
    protected dashboardOverride = inject(DashboardOverrideService);

    protected abstract reload(): void;

    protected syncFromConfig(): void {}
    protected onInit(): void {}
    protected onDestroy(): void {}

    ngOnInit(): void {
        this.widgetConfig$.next(this.widgetConfig);
        this.syncFromConfig();
        this.widgetOrgEffect.bind(
            this.widgetConfig$.asObservable(),
            this.widgetId,
            () => this.reload(),
            this.destroy$.asObservable(),
        );
        this.onInit();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["widgetConfig"] && !changes["widgetConfig"].firstChange) {
            const prev = changes["widgetConfig"].previousValue as Record<string, unknown> | undefined;
            const prevOverride = (prev?.[ORG_LEVEL_OVERRIDE_KEY] ?? null) as string | null;
            const currOverride = (this.widgetConfig[ORG_LEVEL_OVERRIDE_KEY] ?? null) as string | null;
            this.widgetConfig$.next(this.widgetConfig);
            this.syncFromConfig();
            if (prevOverride === currOverride) {
                this.reload();
            }
        }
    }

    ngOnDestroy(): void {
        if (this.widgetId != null) this.dashboardOverride.clearWidget(this.widgetId);
        this.destroy$.next();
        this.destroy$.complete();
        this.onDestroy();
    }

    refresh(): void {
        this.reload();
    }

    get orgLevelOverrideId(): string | null {
        return (this.widgetConfig[ORG_LEVEL_OVERRIDE_KEY] as string | null | undefined) ?? null;
    }

    onOrgLevelOverrideChange(id: string | null): void {
        this.widgetConfig = { ...this.widgetConfig, [ORG_LEVEL_OVERRIDE_KEY]: id };
        this.widgetConfig$.next(this.widgetConfig);
        if (this.widgetId != null) this.dashboardOverride.setOverride(this.widgetId, id);
        this.configChange.emit(this.widgetConfig);
    }

    protected resolveEffectiveOrgIdAndRecord(): string | undefined {
        const effectiveOrgId = this.widgetOrgResolver.getEffectiveOrgId(this.widgetConfig);
        if (this.widgetId != null) {
            const override = this.widgetConfig[ORG_LEVEL_OVERRIDE_KEY] as string | null | undefined;
            this.dashboardOverride.setOverride(this.widgetId, override ?? null);
        }
        return effectiveOrgId;
    }
}
